/*
  This component is copy/pasted from ArtXYPad, from the App directory. Unlike that component, this one is uncontrolled; it manages its own internal state, rather than accepting props.
*/
import React from 'react';
import { styled } from '@linaria/react';

import { clamp, clampedNormalize, roundToNearest } from '@/utils';
import useBoundingBox from '@/hooks/use-bounding-box';

import DottedBackground from '@/components/Controls/DottedControlBackground';
import VisuallyHidden from '@/components/VisuallyHidden';

interface Props {
  numOfRows?: number;
  numOfCols?: number;
}

const STEP = 0.05;
const WIDTH = 250;
const HEIGHT = 250;

function DemoXYPad({ numOfRows = 10, numOfCols = 10 }: Props) {
  const [value, setValue] = React.useState({ x: 0, y: 0 });
  const { x, y } = value;

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const box = useBoundingBox(wrapperRef);

  const [isDragging, setIsDragging] = React.useState(false);

  const xInPixels = clampedNormalize(x, -1, 1, 8, WIDTH - 8);
  const yInPixels = clampedNormalize(y, -1, 1, 8, HEIGHT - 8);

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    function handleMouseMove(ev: MouseEvent) {
      if (!box) {
        return;
      }

      const [x, y] = [ev.clientX, ev.clientY];

      const relativeX = x - box.left;
      const relativeY = y - box.top;

      setValue({
        x: clampedNormalize(relativeX, 0, box.width, -1, 1),
        y: clampedNormalize(relativeY, 0, box.height, -1, 1),
      });
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, box]);

  function handleStartGrabbing() {
    setIsDragging(true);
  }

  function handleMouseDownInEmptySpace(ev: React.MouseEvent) {
    if (!box) {
      return;
    }

    const relativeX = ev.clientX - box.left;
    const relativeY = ev.clientY - box.top;

    setValue({
      x: clampedNormalize(relativeX, 0, box.width, -1, 1),
      y: clampedNormalize(relativeY, 0, box.height, -1, 1),
    });

    setIsDragging(true);
  }

  function handleTouchMove(ev: React.TouchEvent) {
    if (!box) {
      return;
    }

    ev.preventDefault();
    const touch = ev.touches[0];
    const [x, y] = [touch.clientX, touch.clientY];

    const relativeX = x - box.left;
    const relativeY = y - box.top;

    setValue({
      x: clampedNormalize(relativeX, 0, box.width, -1, 1),
      y: clampedNormalize(relativeY, 0, box.height, -1, 1),
    });
  }

  function handleKeyDown(ev: React.KeyboardEvent) {
    if (ev.key === 'ArrowUp') {
      // Don't allow the page to scroll up/down
      ev.preventDefault();

      setValue({
        x,
        y: roundToNearest(clamp(y - STEP, -1, 1), STEP),
      });
    } else if (ev.key === 'ArrowLeft') {
      setValue({
        x: roundToNearest(clamp(x - STEP, -1, 1), STEP),
        y,
      });
    } else if (ev.key === 'ArrowRight') {
      setValue({
        x: roundToNearest(clamp(x + STEP, -1, 1), STEP),
        y,
      });
    } else if (ev.key === 'ArrowDown') {
      // Don't allow the page to scroll up/down
      ev.preventDefault();

      setValue({
        x,
        y: roundToNearest(clamp(y + STEP, -1, 1), STEP),
      });
    }
  }

  return (
    <OuterWrapper>
      <Wrapper
        ref={wrapperRef}
        style={{
          width: WIDTH,
          height: HEIGHT,
          '--cursor': isDragging ? 'grabbing' : 'pointer',
        }}
        onMouseDown={handleMouseDownInEmptySpace}
        onTouchMove={handleTouchMove}
      >
        <BackgroundWrapper>
          <StyledDottedBackground
            axisLines
            numOfRows={numOfRows}
            numOfCols={numOfCols}
          />
        </BackgroundWrapper>
        <HandleButton
          style={{
            transform: `translate(${xInPixels}px, ${yInPixels}px)`,
            '--cursor': isDragging ? 'grabbing' : 'grab',
          }}
          data-is-dragging={isDragging}
          onMouseDown={handleStartGrabbing}
          onTouchStart={handleStartGrabbing}
          onKeyDown={handleKeyDown}
        >
          <Handle />
          <VisuallyHidden>
            Adjust X/Y position with arrow keys
          </VisuallyHidden>
        </HandleButton>
      </Wrapper>
      <Stats>
        <Value>
          <dt>X:</dt>
          <dd>{x.toFixed(2)}</dd>
        </Value>
        <Value>
          <dt>Y:</dt>
          <dd>{y.toFixed(2)}</dd>
        </Value>
      </Stats>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  position: relative;
  margin-inline: auto;
  margin-bottom: 64px;
  width: ${WIDTH}px;

  @media (max-width: 43.75rem) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
`;

const Stats = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.25rem;
  font-family: var(--font-family-mono);

  @media (max-width: 43.75rem) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    width: ${WIDTH / 16}rem;
    gap: 0.5rem;

    dd {
      width: 4rem;
    }
  }

  @media (min-width: 43.76rem) {
    position: absolute;
    bottom: 0;
    right: -1rem;
    transform: translateX(100%);
  }
`;

const Value = styled.div`
  display: flex;
  gap: 0.25rem;
  @media (max-width: 43.75rem) {
    padding-left: 1rem;
  }
`;

const Wrapper = styled.div`
  --dot-color: var(--basic-gray);
  --dot-opacity: 1;
  --axis-color: var(--basic-gray);
  --border-color: var(--basic-gray);
  position: relative;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  overflow: hidden;
  overflow: clip;
  cursor: var(--cursor);
  touch-action: none;
  transition: --basic-gray 500ms;

  html[data-color-mode='light'] & {
    @property --basic-gray {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(225deg 8% 80%);
    }

    --basic-gray: hsl(225deg 8% 80%);
  }
  html[data-color-mode='dark'] & {
    @property --basic-gray {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(210deg 10% 30%);
    }

    --basic-gray: hsl(210deg 10% 30%);
  }

  html[data-color-mode='light'] &:has(:focus-visible),
  html[data-color-mode='light'] &:has(:active) {
    --basic-gray: hsl(240deg 50% 30%);
    transition: --basic-gray 0ms;
  }
  html[data-color-mode='dark'] &:has(:focus-visible),
  html[data-color-mode='dark'] &:has(:active) {
    --basic-gray: hsl(40deg 100% 80%);
    transition: --basic-gray 0ms;
  }
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--control-gray-900);
  border-radius: 3px;
  border: 3px solid var(--control-gray-900);
`;

const StyledDottedBackground = styled(DottedBackground)`
  border-radius: 4px;
  border: none;

  line {
    stroke-linecap: round;
  }
`;

const HandleButton = styled.button`
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  cursor: var(--cursor);

  &:focus {
    outline: none;
  }
`;

const Handle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--color-text);
  border: 2px solid var(--control-gray-900);
  /* Center the button around the top-left corner of the container */
  transform: translate(-50%, -50%);

  ${Wrapper}:hover & {
    transition: background 600ms;
  }

  html[data-color-mode='light'] & {
    --highlight-color: hsl(240deg 95% 62%);
  }
  html[data-color-mode='dark'] & {
    --highlight-color: hsl(50deg 100% 50%);
  }

  html[data-color-mode='light'] ${Wrapper}:has(:active) & {
    background: var(--highlight-color);
    transition: background 0ms;
  }
  html[data-color-mode='dark'] ${Wrapper}:has(:active) & {
    background: var(--highlight-color);
    transition: background 0ms;
  }

  ${HandleButton}:focus-visible & {
    outline: 2px solid var(--highlight-color);
    outline-offset: 3px;
  }
`;

export default DemoXYPad;
